/**
 * The Units roster's right-click menu: what a unit row or group heading
 * offers (`rosterMenuItems`) and how the "Assign to slot" 3x3 grid reads
 * (`slotGrid`). Assigning only edits the client-local control slots
 * (`assignSlot`), never the formations. Pure (no React).
 */

import type { PlayerId, SimState } from '../sim';
import { isSlotSelection, liveSlot, SLOT_COUNT, type Slots } from './slots';

/** What was right-clicked: one unit row, or a formation group's heading. */
export interface RosterTarget {
  kind: 'unit' | 'group';
  /** The unit id, or the group id (the key of its name). */
  id: string;
  /** The units the menu acts on (the group's members, or the one unit). */
  unitIds: readonly string[];
}

export type RosterAction = 'rename' | 'slot' | 'ungroup' | 'goto' | 'select';

export interface RosterMenuItem {
  action: RosterAction;
  label: string;
  /** The key that does the same without the menu. */
  hint?: string;
}

/** The menu's items for `target`; Ungroup only when its units are in a formation. */
export function rosterMenuItems(state: SimState, target: RosterTarget): RosterMenuItem[] {
  const grouped = target.unitIds.some((id) => state.units[id]?.groupId !== undefined);
  return [
    { action: 'rename', label: 'Rename', hint: 'F2' },
    { action: 'slot', label: 'Assign to slot' },
    ...(grouped ? [{ action: 'ungroup', label: 'Ungroup', hint: 'U' } as const] : []),
    { action: 'goto', label: 'Go to' },
    { action: 'select', label: 'Select' },
  ];
}

/** `current`: the slot already holds exactly these units; `used`: another list (assigning replaces it). */
export type SlotCellState = 'empty' | 'used' | 'current';

export interface SlotCell {
  n: number;
  state: SlotCellState;
  /** Live units in the slot. */
  count: number;
}

/** Slots 1-9 in key order (rows of three), each with whether it is free, taken or already this target. */
export function slotGrid(
  state: SimState,
  playerId: PlayerId,
  slots: Slots,
  unitIds: readonly string[]
): SlotCell[] {
  return Array.from({ length: SLOT_COUNT }, (_, i) => {
    const n = i + 1;
    const units = liveSlot(state, playerId, slots, n);
    const cellState: SlotCellState =
      units.length === 0 ? 'empty' : isSlotSelection(units, unitIds) ? 'current' : 'used';
    return { n, state: cellState, count: units.length };
  });
}
